import get from 'lodash/get';
import toUpper from 'lodash/toUpper';
import toLower from 'lodash/toLower';
import startCase from 'lodash/startCase';

const RADIUS_EARTH_KM = 6371;
const MILES_PER_KM = 0.621371;

const UNIT_KILOMETERS = 'km';

const statesByAbbrev = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const statesByName = Object.entries(statesByAbbrev).reduce((acc, [abbrev, name]) => {
  acc[toLower(name)] = abbrev;
  return acc;
}, {});

// export a copy to prevent accidental modifications affecting other exported functions
export const states = { ...statesByAbbrev };

export function getStateAbbreviation(input) {
  const state = toLower(input);
  return statesByName[state] || startCase(toLower(input));
}

export function getStateForAbbreviation(input) {
  const stateAbbreviation = toUpper(input);
  return statesByAbbrev[stateAbbreviation] || stateAbbreviation;
}

export function enforceCollegeLocationFormat(location) {
  const { street, city, state, postalCode, country } = location;
  /* istanbul ignore if */
  if (!street || !city || !state || !postalCode || !country) return null;
  return `${street}, ${city}, ${state} ${postalCode}, ${country}`;
}

export function formatLocation(city, state) {
  let formattedLocation;
  if (!city && !state) {
    formattedLocation = '';
  } else if (!city) {
    formattedLocation = getStateAbbreviation(state);
  } else if (!state) {
    formattedLocation = startCase(toLower(city));
  } else {
    formattedLocation = `${startCase(toLower(city))}, ${getStateAbbreviation(state)}`;
  }
  return formattedLocation;
}

export function formatLocationByPath(location, cityPath, statePath) {
  const city = get(location, cityPath);
  const state = get(location, statePath);
  return formatLocation(city, state);
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export function distanceBetweenPoints({ originLat, originLng, destinationLat, destinationLng, units }) {
  if (!originLat || !originLng || !destinationLat || !destinationLng) {
    return undefined;
  }

  const latRadianDiff = deg2rad(destinationLat - originLat);
  const lngRadianDiff = deg2rad(destinationLng - originLng);

  let distance = (Math.sin(latRadianDiff / 2) * Math.sin(latRadianDiff / 2)) +
    (Math.cos(deg2rad(originLat)) * Math.cos(deg2rad(destinationLat)) * Math.sin(lngRadianDiff / 2) * Math.sin(lngRadianDiff / 2));
  distance = 2 * Math.atan2(Math.sqrt(distance), Math.sqrt(1 - distance));
  distance *= RADIUS_EARTH_KM;

  return (units && units === UNIT_KILOMETERS) ? distance : (distance * MILES_PER_KM);
}
